/**
 * Standard in-app and desktop notification display.
 * - In-app: always show a toast (vue-sonner).
 * - Desktop: when the tab is not visible (document.hidden), show a browser notification
 *   so the user is notified even when the site is in the background.
 */
import { toast } from 'vue-sonner'
import {
  getDesktopNotificationPermission,
  requestDesktopNotificationPermission,
  showDesktopNotification,
} from '@/lib/desktopNotifications'
import { NOTIFICATION_TYPES } from '@/lib/notificationTypes'

const TOAST_DURATION = 5000
const TOAST_POSITION = 'top-right'

/**
 * Map notification type to toast variant and desktop behavior.
 */
function getToastOptions(type) {
  const base = { duration: TOAST_DURATION, position: TOAST_POSITION, closeButton: true }
  switch (type) {
    case NOTIFICATION_TYPES.SUCCESS:
      return { ...base, className: 'bg-green-600 text-white' }
    case NOTIFICATION_TYPES.WARNING:
    case NOTIFICATION_TYPES.POLICY_OVERDUE:
    case NOTIFICATION_TYPES.POLICY_RENEWAL_SOON:
      return { ...base, className: 'bg-amber-600 text-white' }
    default:
      return { ...base, className: 'bg-blue-600 text-white' }
  }
}

/**
 * Show a standard notification: in-app toast + desktop notification when tab is hidden.
 * Call this when you receive a real-time notification (e.g. from Echo) after adding it to the store.
 *
 * @param {Object} notification - Normalized notification: { title, message, type?, link? }
 * @param {Object} [options]
 * @param {() => void} [options.onDesktopClick] - Called when user clicks the desktop notification (e.g. navigate to link). Window is focused automatically.
 */
export function showInAppAndDesktopNotification(notification, { onDesktopClick } = {}) {
  const title = notification?.title || 'Notification'
  const message = notification?.message || ''

  // In-app toast (always when app is open)
  const toastOpts = getToastOptions(notification?.type)
  toast(title, {
    description: message,
    ...toastOpts,
  })

  // Desktop notification when tab is not visible
  if (typeof document !== 'undefined' && document.hidden) {
    const showDesktop = () => {
      if (getDesktopNotificationPermission() !== 'granted') return
      showDesktopNotification({
        title,
        body: message,
        onClick: onDesktopClick,
      })
    }

    if (getDesktopNotificationPermission() === 'granted') {
      showDesktop()
    } else if (getDesktopNotificationPermission() === 'default') {
      requestDesktopNotificationPermission().then((granted) => {
        if (granted) showDesktop()
      })
    }
  }
}

export {
  requestDesktopNotificationPermission,
  getDesktopNotificationPermission,
  isDesktopNotificationSupported,
} from '@/lib/desktopNotifications'
